/* Vertical video reviews: muted in-view previews in a rail, sound in a lightbox.

   The one idea worth knowing before reading this: a Shopify-hosted clip has
   exactly ONE <video> element, which lives in its card and is *moved* into the
   lightbox on open and moved back on close. Rendering a second player for the
   lightbox would double the markup and re-download the file; reparenting keeps
   the buffered data and the playhead. It has to happen in a single task —
   appendChild does the remove and the insert atomically, which is why nothing
   here ever calls removeChild first. */
(function () {
  'use strict';

  var PREVIEW_RATIO = 0.6;

  function prefersQuiet() {
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var saveData = navigator.connection && navigator.connection.saveData;
    return reduce || !!saveData;
  }

  function lockScroll(lock) {
    var tb = window.Miniflex;
    if (tb && typeof tb.lockScroll === 'function') tb.lockScroll(lock);
  }

  function embedSrc(provider, id, url) {
    if (provider === 'youtube' && id) {
      return (
        'https://www.youtube-nocookie.com/embed/' +
        id +
        '?autoplay=1&rel=0&modestbranding=1&playsinline=1'
      );
    }
    if (provider === 'vimeo' && id) {
      return 'https://player.vimeo.com/video/' + id + '?autoplay=1&dnt=1';
    }
    return url;
  }

  class Reels extends HTMLElement {
    connectedCallback() {
      this.reels = Array.prototype.slice.call(this.querySelectorAll('[data-reel]'));
      if (!this.reels.length) return;

      this.rail = this.querySelector('[data-reels-rail]');
      this.lb = this.querySelector('[data-reels-lb]');
      this.stage = this.querySelector('[data-reels-stage]');
      this.metaEl = this.querySelector('[data-reels-meta]');
      this.soundBtn = this.querySelector('[data-reels-sound]');

      this.current = -1;
      this.moved = null; // { el, home } while a card's <video> is on the stage
      this.iframe = null;
      this.opener = null;
      this.soundOn = true;

      this.bindCards();
      this.bindLightbox();
      this.watchViewport();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      this.returnVideo();
    }

    /* ------------------------------------------------------------ cards -- */

    bindCards() {
      this.reels.forEach(
        function (reel, i) {
          var btn = reel.querySelector('[data-reel-open]');
          if (btn) {
            btn.addEventListener(
              'click',
              function () {
                this.open(i, btn);
              }.bind(this)
            );
          }
        }.bind(this)
      );

      /* Selecting a block in the theme editor should bring its card into view —
         otherwise the merchant edits a reel they cannot see. */
      document.addEventListener(
        'shopify:block:select',
        function (e) {
          if (!this.contains(e.target)) return;
          var card = e.target.closest ? e.target.closest('[data-reel]') : null;
          if (card && card.scrollIntoView) {
            card.scrollIntoView({ block: 'nearest', inline: 'center' });
          }
        }.bind(this)
      );
    }

    /* Preview playback is a nicety, so it gives up quietly: reduced motion,
       Data Saver, or no IntersectionObserver all just leave the poster up. */
    watchViewport() {
      if (this.dataset.preview !== 'true') return;
      if (prefersQuiet() || typeof IntersectionObserver === 'undefined') return;

      this.io = new IntersectionObserver(
        function (entries) {
          entries.forEach(
            function (entry) {
              var video = entry.target.querySelector('.reel__el');
              if (!video) return;
              // Nothing in the rail plays while the lightbox has the floor.
              if (entry.isIntersecting && !this.isOpen()) {
                this.startPreview(entry.target, video);
              } else {
                video.pause();
              }
            }.bind(this)
          );
        }.bind(this),
        { threshold: PREVIEW_RATIO }
      );

      this.reels.forEach(
        function (reel) {
          if (reel.dataset.reelKind === 'hosted') this.io.observe(reel);
        }.bind(this)
      );
    }

    startPreview(reel, video) {
      video.muted = true;
      var attempt = video.play();
      if (attempt && attempt.then) {
        attempt
          .then(function () {
            reel.dataset.previewing = 'true';
          })
          .catch(function () {
            /* Autoplay refused — the poster stays, which is a fine outcome. */
          });
      } else {
        reel.dataset.previewing = 'true';
      }
    }

    /* -------------------------------------------------------- lightbox -- */

    isOpen() {
      return !!(this.lb && this.lb.open);
    }

    bindLightbox() {
      if (!this.lb) return;

      var close = this.querySelector('[data-reels-close]');
      var prev = this.querySelector('[data-reels-prev]');
      var next = this.querySelector('[data-reels-next]');

      if (close) close.addEventListener('click', this.close.bind(this));
      if (prev) prev.addEventListener('click', this.step.bind(this, -1));
      if (next) next.addEventListener('click', this.step.bind(this, 1));
      if (this.soundBtn) this.soundBtn.addEventListener('click', this.toggleSound.bind(this));

      // Esc fires `cancel` before `close`; intercept so teardown always runs.
      this.lb.addEventListener(
        'cancel',
        function (e) {
          e.preventDefault();
          this.close();
        }.bind(this)
      );

      // Clicking the backdrop lands on the dialog itself, never on its content.
      this.lb.addEventListener(
        'click',
        function (e) {
          if (e.target === this.lb) this.close();
        }.bind(this)
      );

      this.lb.addEventListener(
        'keydown',
        function (e) {
          if (e.key === 'ArrowRight') this.step(1);
          if (e.key === 'ArrowLeft') this.step(-1);
        }.bind(this)
      );
    }

    open(index, opener) {
      if (!this.lb) return;
      this.opener = opener || null;

      // Silence every rail preview before the one with sound starts.
      this.reels.forEach(function (reel) {
        var v = reel.querySelector('.reel__el');
        if (v) v.pause();
      });

      this.mount(index);

      if (typeof this.lb.showModal === 'function') {
        this.lb.showModal();
      } else {
        this.lb.setAttribute('open', '');
      }
      lockScroll(true);
    }

    close() {
      if (!this.lb) return;
      this.teardown();
      if (typeof this.lb.close === 'function' && this.lb.open) {
        this.lb.close();
      } else {
        this.lb.removeAttribute('open');
      }
      lockScroll(false);

      if (this.opener) {
        this.opener.focus();
        this.opener = null;
      }

      // Whatever is on screen can resume its silent loop.
      if (this.io) {
        this.reels.forEach(
          function (reel) {
            if (reel.dataset.reelKind !== 'hosted') return;
            this.io.unobserve(reel);
            this.io.observe(reel);
          }.bind(this)
        );
      }
    }

    step(delta) {
      if (this.current < 0) return;
      var n = this.reels.length;
      this.mount((this.current + delta + n) % n);
    }

    mount(index) {
      this.teardown();

      var reel = this.reels[index];
      if (!reel) return;
      this.current = index;

      if (reel.dataset.reelKind === 'hosted') {
        var video = reel.querySelector('.reel__el');
        if (video) {
          this.moved = { el: video, home: reel.querySelector('[data-reel-player]') };
          this.stage.appendChild(video); // atomic move — see file header
          video.controls = true;
          video.muted = !this.soundOn;
          video.currentTime = 0;
          var attempt = video.play();
          if (attempt && attempt.catch) {
            attempt.catch(
              function (err) {
                /* Only an autoplay refusal is worth retrying muted. A missing
                   source or a decode failure rejects here too, and demoting the
                   whole session to silent over one broken file would be wrong —
                   the shopper still has visible controls either way. */
                if (!err || err.name !== 'NotAllowedError') return;
                video.muted = true;
                this.soundOn = false;
                this.syncSound();
                var retry = video.play();
                if (retry && retry.catch) retry.catch(function () {});
              }.bind(this)
            );
          }
        }
        if (this.soundBtn) this.soundBtn.hidden = false;
      } else {
        this.iframe = document.createElement('iframe');
        this.iframe.className = 'reels-lb__el';
        this.iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
        this.iframe.setAttribute('allowfullscreen', '');
        this.iframe.setAttribute('title', reel.dataset.reelCreator || 'Video');
        this.iframe.src = embedSrc(
          reel.dataset.reelProvider,
          reel.dataset.reelVideoId,
          reel.dataset.reelUrl
        );
        this.stage.appendChild(this.iframe);
        // The embed owns its own audio; our button would be lying.
        if (this.soundBtn) this.soundBtn.hidden = true;
      }

      this.syncSound();
      this.writeMeta(reel);
    }

    writeMeta(reel) {
      if (!this.metaEl) return;
      var who = reel.dataset.reelCreator || '';
      var caption = reel.dataset.reelCaption || '';
      this.metaEl.textContent = who && caption ? who + ' — ' + caption : who || caption;
      this.metaEl.hidden = !this.metaEl.textContent;
    }

    /* Returns a borrowed <video> to its card and clears anything else on the
       stage. Safe to call when nothing is mounted. */
    teardown() {
      this.returnVideo();
      if (this.iframe) {
        this.iframe.remove();
        this.iframe = null;
      }
      if (this.stage) this.stage.innerHTML = '';
    }

    returnVideo() {
      if (!this.moved) return;
      var video = this.moved.el;
      var home = this.moved.home;
      this.moved = null;

      video.pause();
      video.controls = false;
      video.muted = true;
      if (home) home.appendChild(video);
    }

    toggleSound() {
      this.soundOn = !this.soundOn;
      if (this.moved) this.moved.el.muted = !this.soundOn;
      this.syncSound();
    }

    syncSound() {
      if (!this.soundBtn) return;
      this.soundBtn.dataset.on = this.soundOn ? 'true' : 'false';
      this.soundBtn.setAttribute(
        'aria-label',
        this.soundBtn.dataset[this.soundOn ? 'labelMute' : 'labelUnmute'] ||
          this.soundBtn.getAttribute('aria-label') ||
          ''
      );
    }
  }

  if (!customElements.get('tb-reels')) customElements.define('tb-reels', Reels);
})();
